import { XeroDisabledLogo, XeroLogo } from '@approvalmax/ui';
import { Box, Flex, Radio, Text } from '@approvalmax/ui/src/components';
import { ComponentType, memo, MemoExoticComponent, useCallback, useMemo, useRef } from 'react';
import { SelectableCard } from 'shared/components';
import { disabledClassName, selectedClassName } from 'shared/constants';

import { accountParams } from './BankAccountCard.constants';
import { Loading } from './BankAccountCard.loading';
import { messages } from './BankAccountCard.messages';
import { StyledImg } from './BankAccountCard.styles';
import { BankAccountCardProps, ChildrenComponents } from './BankAccountCard.types';

export const BankAccountCard = memo((props) => {
    const { selected = false, hasCurrencyMismatch = false, bankAccount } = props;

    const isMissingXeroBankAccount = !bankAccount.xeroBankAccount;
    const disabled = hasCurrencyMismatch || isMissingXeroBankAccount || !bankAccount.isConnected;

    const radioRef = useRef<HTMLInputElement | null>(null);

    const handleMouseEnter = useCallback(() => {
        radioRef.current?.focus();
    }, []);

    const handleMouseLeave = useCallback(() => {
        radioRef.current?.blur();
    }, []);

    const handleClick = useCallback(() => {
        radioRef.current?.click();
    }, []);

    const filledAccountParams = useMemo(() => {
        return accountParams.filter((param) => bankAccount.identification[param]);
    }, [bankAccount.identification]);

    const className = `${disabled ? disabledClassName : ''} ${selected ? selectedClassName : ''}`;

    return (
        <SelectableCard
            tabIndex={0}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            // we need a class here because we need to tie to something within `has()`
            className={className}
        >
            <Box spacing='12 0'>
                <Flex wrap={false} justifyContent='space-between'>
                    <StyledImg src={bankAccount.bank.iconUrl} width={28} height={28} alt='logo' $disabled={disabled} />

                    <Flex direction='column' spacing='8' grow={1}>
                        <Text
                            font='label'
                            fontSize='small'
                            fontWeight='medium'
                            color={disabled ? 'midnight60' : 'midnight80'}
                        >
                            {bankAccount.nickname}
                        </Text>

                        <Flex direction='column' spacing='4' grow={1}>
                            {filledAccountParams.map((prop, index) => (
                                <Flex key={prop} spacing='8'>
                                    <Text font='label' fontSize='xsmall' color={disabled ? 'midnight60' : 'midnight80'}>
                                        {messages[prop]({ value: bankAccount.identification[prop] })}
                                    </Text>

                                    {index === 0 && !bankAccount.isConnected && (
                                        <Text color='red100' font='label' fontSize='xsmall' uppercase>
                                            {messages.disconnected}
                                        </Text>
                                    )}
                                </Flex>
                            ))}
                        </Flex>

                        <Flex spacing='4' alignItems='center'>
                            {disabled ? (
                                <XeroDisabledLogo width={16} height={16} />
                            ) : (
                                <XeroLogo width={16} height={16} />
                            )}

                            <Text
                                font='label'
                                fontSize='xsmall'
                                color={
                                    bankAccount.xeroBankAccount ? (disabled ? 'midnight60' : 'midnight80') : 'red100'
                                }
                            >
                                {bankAccount.xeroBankAccount?.name || messages.noXeroBankAccount}
                            </Text>
                        </Flex>
                    </Flex>

                    <Flex direction='column'>
                        <Flex alignItems='center'>
                            <Text font='label' fontSize='xsmall' color={disabled ? 'midnight60' : 'midnight80'}>
                                {bankAccount.currencyCode}
                            </Text>

                            <Radio value={bankAccount.id} ref={radioRef} disabled={disabled} />
                        </Flex>
                    </Flex>
                </Flex>
            </Box>
        </SelectableCard>
    );
}) as MemoExoticComponent<ComponentType<BankAccountCardProps>> & ChildrenComponents;

BankAccountCard.displayName = 'BankAccountCard';

BankAccountCard.Loading = Loading;
